import { useState, useEffect } from 'react';
import Cookies from 'universal-cookie';

import type { AppConfig } from 'src/components/App/AppConfigProvider';
import type { ExperimentToggleNames } from 'src/config';
import { useVisitorId } from 'src/hooks/useUserCookies';
import logger from 'src/modules/logger';

interface SeekExperimentLink {
  href: string;
}

interface SeekExperiment {
  name: string;
  variation: {
    index: number;
    name: string;
  };
  _links: {
    conversion: SeekExperimentLink;
    impression: SeekExperimentLink;
  };
}

type SeekExperiments = Record<string, SeekExperiment>;

interface ExperimentStore {
  id: SeekExperiment['name'];
  variant: SeekExperiment['variation']['index'];
}

type ExperimentToggles = Record<ExperimentToggleNames, boolean>;

const cookies = new Cookies();

const getExperiments = async (
  experimentApiUrl: string,
  userId: string,
): Promise<SeekExperiments> => {
  const experimentsUrl = `${experimentApiUrl}/${userId}`;
  try {
    const response = await fetch(experimentsUrl);
    const { experiments } = await response.json();

    return experiments as SeekExperiments;
  } catch (caughtError) {
    logger('Failed to fetch Experiments', { error: caughtError as Error });
    return {};
  }
};

const getExperimentCookies = (
  configs: AppConfig['experimentsConfig'],
): Record<string, number> =>
  Object.values(configs).reduce((acc, config) => {
    const value = cookies.get(config.overrideCookieName);

    if (!value) {
      return acc;
    }
    return {
      ...acc,
      [config.overrideCookieName]: !isNaN(Number(value)) ? Number(value) : 1,
    };
  }, {});

const useExperiments = ({
  experimentEnabled,
  experimentsConfig,
  experimentApiUrl,
  cookieDomain,
}: Pick<
  AppConfig,
  | 'experimentsConfig'
  | 'experimentEnabled'
  | 'experimentApiUrl'
  | 'cookieDomain'
>) => {
  const [isExperimentReady, setIsExperimentReady] = useState(false);
  const [experimentsStore, setExperimentsStore] = useState<ExperimentStore[]>(
    [],
  );
  const [experimentToggles, setExperimentToggles] = useState<ExperimentToggles>(
    Object.keys(experimentsConfig).reduce(
      (acc, name) => ({
        ...acc,
        [name]: false,
      }),
      {} as ExperimentToggles,
    ),
  );
  const visitorId = useVisitorId();

  useEffect(() => {
    (async () => {
      if (!experimentEnabled) {
        setIsExperimentReady(true);
        return;
      }

      let experimentCookies = getExperimentCookies(experimentsConfig);
      // if the cookie length is not match, fetch api again
      if (
        Object.values(experimentCookies).length !==
        Object.values(experimentsConfig).length
      ) {
        const experiments = await getExperiments(experimentApiUrl, visitorId);

        experimentCookies = Object.values(experimentsConfig).reduce(
          (acc, { id, overrideCookieName }) => {
            const overridenCookieValue = experimentCookies[overrideCookieName];

            const experimentsApiVariation = experiments[id]
              ? experiments[id].variation.index
              : 0;
            const variation = overridenCookieValue || experimentsApiVariation;
            cookies.set(overrideCookieName, variation, {
              secure: location?.protocol === 'https',
              domain: cookieDomain,
              maxAge: 2147483647,
            });

            return {
              ...acc,
              [overrideCookieName]: Number(variation),
            };
          },
          {},
        );
      }

      setExperimentsStore(
        Object.values(experimentsConfig).map(
          ({ name, overrideCookieName }) => ({
            id: name,
            variant: experimentCookies[overrideCookieName],
          }),
        ),
      );

      setExperimentToggles(
        Object.entries(experimentsConfig).reduce(
          (acc, [toggleName, { overrideCookieName }]) => ({
            ...acc,
            [toggleName]: experimentCookies[overrideCookieName] > 0,
          }),
          {} as ExperimentToggles,
        ),
      );

      setIsExperimentReady(true);
    })();
  }, [
    experimentApiUrl,
    experimentEnabled,
    experimentsConfig,
    cookieDomain,
    visitorId,
  ]);

  return {
    isExperimentReady,
    ...experimentToggles,
    experiments: experimentsStore,
  };
};
export default useExperiments;
